/**
 * ==============================================
 * ADD TO CART HANDLER - RECHARGE INTEGRATION
 * ==============================================
 *
 * This module handles the cart operations for the subscription picker.
 * It listens for custom events from MonthsSubscriptionPicker and:
 * 1. Processes selected scents through Recharge bundle system
 * 2. Adds shower pack (if applicable) with correct subscription plan
 * 3. Adds any selected add-ons
 * 4. Adds all items to Shopify cart
 * 5. Redirects to checkout
 *
 * RECHARGE INTEGRATION:
 * - Uses Recharge CDN bundle settings for scent selection
 * - Applies correct selling plans for subscriptions
 * - Bundles multiple scents into single cart item with Recharge metadata
 *
 * CART ITEM STRUCTURE:
 * - Each item has variant ID, quantity, and optional selling_plan
 * - All items get unique _bundle_id property (timestamp) for grouping
 * - Add-ons are separate cart items without subscription plans
 *
 * DEPENDENCIES:
 * - recharge.cdn global object (loaded from Recharge CDN)
 * - theme.routes global object (Shopify routes)
 * - MonthsSubscriptionPicker component (dispatches events)
 */
(async function () {
  /**
   * Check if customer already has shower pack
   * Affects which variants and pricing to use
   */
  const refillProductOnly = document.querySelector('[data-refill-product-only]')?.dataset.refillProductOnly === 'true';

  /**
   * Load product configuration from JSON script tag
   * Contains variant IDs, bundle settings, images, etc.
   */
  const productsData = JSON.parse(document.getElementById('months-product-subscription-picker-data').textContent);

  /**
   * Fetch Recharge bundle settings for scent selection
   * Monthly and quarterly (Discovery Plan) may use different Recharge bundle products.
   * Both are loaded upfront; the correct one is selected at add-to-cart time.
   */
  const monthlyBundleProductId = productsData.scents_bundle_product_id;
  const monthlyBundleVariantId = productsData.scents_bundle_product_variants[0].id;
  const monthlyBundleSettings = await recharge.cdn.getCDNBundleSettings(monthlyBundleProductId);
  const monthlyBundleCollectionId = monthlyBundleSettings.variants[0].option_sources[0].collection_id;

  // Discovery/Quarterly bundle — falls back to monthly bundle if not configured
  const discoveryBundleProductId = productsData.discovery_scents_bundle_product_id || monthlyBundleProductId;
  const discoveryBundleVariantId = productsData.discovery_scents_bundle_product_variants
    ? productsData.discovery_scents_bundle_product_variants[0].id
    : monthlyBundleVariantId;

  let discoveryBundleCollectionId = monthlyBundleCollectionId;
  if (discoveryBundleProductId !== monthlyBundleProductId) {
    const discoveryBundleSettings = await recharge.cdn.getCDNBundleSettings(discoveryBundleProductId);
    discoveryBundleCollectionId = discoveryBundleSettings.variants[0].option_sources[0].collection_id;
  }

  /**
   * Shower pack variants (applicator + wall mount + refill)
   *
   * VARIANT STRUCTURE:
   * The shower pack product has multiple variants organized by:
   * 1. Subscription type (determines selling plan attachment)
   * 2. Color (e.g., Black, Cream - filtered later based on selection)
   *
   * INDEX MAPPING (when filtered by color):
   * [0] = One-time purchase variant (no selling_plan_allocations)
   * [1] = Monthly subscription variant (selling_plan_allocations[0] = monthly plan)
   * [2] = Quarterly subscription variant (selling_plan_allocations[1] = quarterly plan)
   *
   * Note: "varaints" spelling is intentional (legacy naming in data structure)
   *
   * FALLBACK CHAIN:
   * 1. window.shower_shower_pack_product_varaints (allows external override)
   * 2. productsData.shower_shower_pack_product_varaints (from JSON config)
   * 3. Empty array (safe fallback)
   */
  const shower_shower_pack_product_varaints = window.shower_shower_pack_product_varaints || productsData.shower_shower_pack_product_varaints || [];

  /**
   * Discovery Plan shower pack product (e.g. "the-discovery-plan")
   * Used instead of regular shower pack when quarterly is selected.
   * Falls back to regular shower pack if not configured.
   */
  const discovery_shower_pack_product_varaints = productsData.discovery_shower_pack_product_varaints || shower_shower_pack_product_varaints;

  /**
   * ==============================================
   * EVENT LISTENER: Add to Cart Requested
   * ==============================================
   * Triggered when MonthsSubscriptionPicker's handleAddToCart() dispatches event
   *
   * EVENT PAYLOAD (e.detail):
   * - refillFrequency: 'monthly' | 'quarterly' | 'trial' | 'oneoff'
   * - selectedScents: Array of scent objects with variantId and quantity
   * - selectedAddons: Array of add-on products
   * - productData: Pricing option from prices_map
   * - pricing: Price breakdown object
   */
  document.body.addEventListener('subscription-picker:addToCartRequested', async (e) => {
    const payloadData = e.detail;
    //console.log('payloadData', payloadData);
    const picker = e.target;
    const addToCartBtn = picker.querySelector('[data-add-to-cart-btn]');

    // Disable button during async operation
    updateButtonState(addToCartBtn, true);

    /**
     * ==============================================
     * STEP 1: SELECT SHOWER PACK VARIANT
     * ==============================================
     * Choose correct variant based on refill frequency and selected color.
     *
     * QUARTERLY (Discovery Plan):
     *   Uses "the-discovery-plan" product — one variant per color, index 0.
     *   Selling plans: [0] = monthly, [1] = quarterly.
     *
     * MONTHLY / ONE-TIME / GIFT:
     *   Uses regular shower pack — variants per subscription type × color.
     *   After color filter: [0] = one-time, [1] = monthly, [3] = gift.
     */
    const isQuarterly = payloadData.refillFrequency === 'quarterly';
    const isTrial = payloadData.refillFrequency === 'trial';

    let activeShowerPackVariants, activeColorOption;
    if (isQuarterly) {
      activeShowerPackVariants = discovery_shower_pack_product_varaints;
      activeColorOption = productsData.discovery_color_option || productsData.color_option;
    } else {
      // Monthly, one-time, gift, and trial all use the regular shower pack
      activeShowerPackVariants = shower_shower_pack_product_varaints;
      activeColorOption = productsData.color_option;
    }

    // Filter variants by selected color if color is selected
    let filteredVariants = activeShowerPackVariants;
    if (payloadData.selectedColor && activeColorOption) {
      const colorOptionPosition = activeColorOption.position;
      const optionKey = `option${colorOptionPosition}`;

      filteredVariants = activeShowerPackVariants.filter((variant) => {
        return variant[optionKey] === payloadData.selectedColor;
      });

      //console.log('Filtered variants by color:', payloadData.selectedColor, JSON.stringify(filteredVariants));
    }

    let showerPackVariantIndex;
    if (isQuarterly) {
      // Discovery plan product: one variant per color, always index 0
      showerPackVariantIndex = 0;
    } else if (isTrial) {
      // Trial applicator: one-time variant at index 4 in regular shower pack
      showerPackVariantIndex = 4;
    } else {
      showerPackVariantIndex = payloadData.refillFrequency == 'oneoff' || payloadData.refillFrequency == 'gift' ? 0 : 1;

      // add 20% discount for gift purchases
      if (payloadData.refillFrequency === 'gift' && filteredVariants?.length > 3) {
        // make sure we have gift variant
        showerPackVariantIndex = 3;
      }
    }

    const shower_shower_pack_varaint = filteredVariants?.[showerPackVariantIndex];
    const showerPackVariantId = shower_shower_pack_varaint?.id;

    /**
     * Get selling plan ID for shower pack (subscriptions only)
     * - Monthly: First selling plan allocation
     * - Quarterly: Second selling plan allocation (same convention for both products)
     * - One-time: Empty string (no subscription)
     */
    let showerPackSellingPlanId = '';
    if (payloadData.refillFrequency === 'monthly') {
      showerPackSellingPlanId = shower_shower_pack_varaint?.selling_plan_allocations?.[0]?.selling_plan_id || '';
    } else if (payloadData.refillFrequency === 'quarterly') {
      showerPackSellingPlanId = shower_shower_pack_varaint?.selling_plan_allocations?.[1]?.selling_plan_id || '';
    }
    // Trial: no selling plan on applicator — it's added as one-time.
    // The subscription is managed by the dynamic bundle (14-days-trial-bundle).

    /**
     * ==============================================
     * STEP 2: BUILD RECHARGE BUNDLE FOR SCENTS
     * ==============================================
     * Recharge bundles allow customers to select multiple scent variants
     * that are packaged as a single "bundle" product in the cart.
     *
     * BUNDLE STRUCTURE:
     * - externalProductId: Bundle product ID
     * - externalVariantId: Bundle variant ID
     * - selections: Array of selected scent variants with quantities
     *
     * For subscriptions, each selection includes a sellingPlan ID.
     * For one-time purchases, sellingPlan is removed.
     */
    // Select correct bundle product based on frequency (isQuarterly declared in STEP 1)
    let activeBundleProductId, activeBundleVariantId, activeBundleCollectionId;
    if (isQuarterly) {
      activeBundleProductId = discoveryBundleProductId;
      activeBundleVariantId = discoveryBundleVariantId;
      activeBundleCollectionId = discoveryBundleCollectionId;
    } else {
      activeBundleProductId = monthlyBundleProductId;
      activeBundleVariantId = monthlyBundleVariantId;
      activeBundleCollectionId = monthlyBundleCollectionId;
    }

    let bundleItems = [];
    if (payloadData.selectedScents && payloadData.selectedScents.length > 0) {
      if (isTrial) {
        // Trial bundle: match selected scent to a variant in the trial bundle product
        // Each variant represents a scent (e.g. "The Modern Man", "The Evening Man")
        const trialVariants = productsData.trial_scent_bundle_product_variants || [];
        bundleItems = payloadData.selectedScents
          .map((s) => {
            const scentTitle = s.title.replace(/^Scent:\s*/i, '').trim();
            const matchedVariant = trialVariants.find((v) => v.title.toLowerCase().includes(scentTitle.toLowerCase()));
            if (!matchedVariant) {
              console.warn('No matching trial variant found for scent:', s.title);
              return null;
            }
            return {
              id: matchedVariant.id,
              quantity: s.quantity,
              selling_plan: matchedVariant.selling_plan_allocations?.[0]?.selling_plan_id || '',
            };
          })
          .filter(Boolean);
      } else {
        const bundle = {
          externalProductId: activeBundleProductId,
          externalVariantId: activeBundleVariantId,
          selections: payloadData.selectedScents.map((scentSelection) => ({
            collectionId: activeBundleCollectionId,
            externalProductId: scentSelection.variantId, // Scent product ID
            externalVariantId: scentSelection.variantId, // Scent variant ID
            quantity: scentSelection.quantity,
            sellingPlan: scentSelection.sellingPlanId, // Subscription plan (if applicable)
          })),
        };

        // Remove selling plans for one-time purchases and gifts
        if (payloadData.refillFrequency == 'oneoff' || payloadData.refillFrequency == 'gift') {
          bundle.selections.forEach((selection) => {
            delete selection.sellingPlan;
          });
        }

        // Convert bundle configuration to Shopify cart items via Recharge
        bundleItems = await recharge.bundle.getDynamicBundleItems(bundle, 'shopifyProductHandle');
      }
    } else {
      console.warn('No scents selected for cart addition');
    }

    /**
     * ==============================================
     * STEP 3: BUILD COMPLETE ITEMS ARRAY
     * ==============================================
     * Combine all cart items:
     * 1. Bundle items (scents from Recharge)
     * 2. Shower pack (if not refill-only customer)
     * 3. Add-ons (extra products for one-time purchases)
     */
    const items = [...bundleItems];

    /**
     * Add shower pack for first-time customers and gift purchases
     * - Not added if refillProductOnly is true (existing customers)
     * - Always added for gift purchases (gifts include full starter pack)
     * - Includes selling plan for subscriptions only
     */
    if (!refillProductOnly || payloadData.refillFrequency === 'gift') {
      const showerPackItem = {
        id: showerPackVariantId,
        quantity: 1,
      };

      // Add subscription plan for recurring orders
      if (payloadData.refillFrequency !== 'oneoff' && showerPackSellingPlanId) {
        showerPackItem.selling_plan = showerPackSellingPlanId;
      }

      items.push(showerPackItem);
    }

    /**
     * Add selected add-ons (one-time purchases only)
     * Add-ons are additional products without subscription plans
     */
    if (payloadData.selectedAddons && payloadData.selectedAddons.length > 0) {
      payloadData.selectedAddons.forEach((addon) => {
        items.push({
          id: addon.id,
          quantity: addon.quantity,
        });
      });
    }

    // Safety check: Ensure we have items to add
    if (items.length == 0) {
      alert(`Something went wrong. Please try again.`);
      return;
    }

    /**
     * ==============================================
     * STEP 4: ADD BUNDLE TRACKING & CREATE SEPARATE LINE ITEMS PER RECIPIENT
     * ==============================================
     * For gift purchases with multiple recipients:
     * - Create separate line items for each recipient
     * - Each line item gets unique properties for that specific recipient
     * - This allows easy tracking and fulfillment in Shopify
     *
     * For non-gift or single recipient:
     * - Add standard bundle tracking properties
     */
    const bundleId = new Date().getTime();
    let itemsWithProperties = [];

    if (payloadData.refillFrequency === 'gift' && payloadData.giftNotification?.recipients) {
      const recipients = payloadData.giftNotification.recipients;
      //console.log(`Creating separate line items for ${recipients.length} recipients`);

      // For each recipient, duplicate all items with their unique properties
      recipients.forEach((recipient, index) => {
        // Filter shower pack variants by THIS recipient's color
        let recipientFilteredVariants = filteredVariants;
        let recipientShowerPackVariant = shower_shower_pack_varaint;

        if (recipient.recipientColor && productsData.color_option) {
          const colorOptionPosition = productsData.color_option.position;
          const optionKey = `option${colorOptionPosition}`;

          recipientFilteredVariants = shower_shower_pack_product_varaints.filter((variant) => {
            return variant[optionKey] === recipient.recipientColor;
          });

          // Get the variant for this recipient's color
          recipientShowerPackVariant = recipientFilteredVariants?.[showerPackVariantIndex];
          //console.log(`Recipient ${index + 1} color: ${recipient.recipientColor}, variant:`, recipientShowerPackVariant?.id);
        }

        const recipientShowerPackVariantId = recipientShowerPackVariant?.id;

        const recipientItems = items.map((item) => ({
          ...item,
          // Replace shower pack variant ID with recipient-specific color variant
          id: item.id === showerPackVariantId ? recipientShowerPackVariantId : item.id,
          properties: {
            ...item.properties,
            _bundle_id: bundleId,
            _is_gift: 'Yes',
            _recipient_count: recipients.length.toString(),
            _recipient_number: (index + 1).toString(), // Track which recipient this is for

            // Unique properties for THIS recipient (no numbering)
            _Recipient_colour: recipient.recipientColor,
            _Recipient_email: recipient.recipientEmail,
            _Recipient_first_name: recipient.recipientFirstName,
            _Recipient_occasion: recipient.occasion,
            _Recipient_klaviyo_list_id: recipient.klaviyoListId,
            _Recipient_klaviyo_list_name: recipient.klaviyoListName,

            // Add date if present
            ...(recipient.day &&
              recipient.month && {
                _Recipient_date: `${recipient.day}/${recipient.month}`,
              }),
          },
        }));

        itemsWithProperties.push(...recipientItems);
      });

      //console.log(`Created ${itemsWithProperties.length} total line items for ${recipients.length} recipients`);
    } else {
      // Non-gift or no recipients: standard bundle properties
      itemsWithProperties = items.map((item) => ({
        ...item,
        properties: {
          ...item.properties,
          _bundle_id: bundleId,
        },
      }));
    }

    //console.log('itemsWithProperties', itemsWithProperties);

    /**
     * ==============================================
     * STEP 5: ADD ITEMS TO SHOPIFY CART
     * ==============================================
     * Use Shopify AJAX Cart API to add all items at once.
     * Includes cart attributes for tracking customer type.
     */
    // Optional: Clear cart first (currently commented out)
    //await fetch(`${theme.routes.cart_clear_url}.js`);

    const response = await fetch(`${theme.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: itemsWithProperties,
        attributes: {
          'data-refill-product-only': refillProductOnly.toString(), // Track customer type
        },
      }),
    });
    const data = await response.json();
    //console.log('data', data);

    /**
     * ==============================================
     * STEP 5.4: META PIXEL ADD TO CART
     * ==============================================
     * Fire a single AddToCart event for the bundle (Shopify's automatic pixel
     * does not run for this fetch-based add). Our meta-add-to-cart-bundle-fix
     * snippet will pass it through as-is.
     */
    if (response.ok && typeof window.fbq === 'function') {
      const pricing = payloadData.pricing || {};
      const valueCents = (pricing.firstOrderPrice || 0) + (pricing.addonsTotal || 0);
      const numItems = itemsWithProperties.reduce((sum, i) => sum + (i.quantity || 1), 0);
      const contentIds = itemsWithProperties.flatMap((i) =>
        Array(i.quantity || 1)
          .fill()
          .map(() => String(i.id))
      );
      window.fbq('track', 'AddToCart', {
        content_ids: contentIds.length ? contentIds : undefined,
        content_type: 'product',
        value: valueCents / 100,
        currency: 'GBP',
        num_items: numItems,
      });
    }

    /**
     * ==============================================
     * STEP 5.5: SEND GIFT RECIPIENTS TO KLAVIYO
     * ==============================================
     * If this is a gift purchase with recipients,
     * send each recipient's data to Klaviyo for 30-day follow-up.
     * This happens after cart is successfully created.
     * The widget's sendToKlaviyo() method handles all recipients.
     */
    /*     if (payloadData.refillFrequency === 'gift' && payloadData.giftNotification?.recipients?.length > 0) {
      const recipientCount = payloadData.giftNotification.recipients.length;
      //console.log(`Sending ${recipientCount} gift recipient(s) to Klaviyo...`);

      // Get reference to gift notification widget
      const giftWidget = document.querySelector('gift-notification-widget');
      if (giftWidget && typeof giftWidget.sendToKlaviyo === 'function') {
        try {
          //await giftWidget.sendToKlaviyo(); // Handles all recipients internally
          //console.log(`Successfully sent ${recipientCount} recipient(s) to Klaviyo`);
        } catch (error) {
          // Log error but don't block checkout
          console.error('Failed to send gift recipients to Klaviyo:', error);
        }
      } else {
        console.warn('Gift notification widget not found or sendToKlaviyo method not available');
      }
    } */

    // Re-enable button after operation
    updateButtonState(addToCartBtn, false);

    /**
     * ==============================================
     * STEP 6: REDIRECT TO CHECKOUT
     * ==============================================
     * Programmatically click hidden form's checkout button.
     * This triggers Shopify's checkout flow with cart items.
     */
    document.querySelector('#subscription-cart-form button[name="checkout"]').click();
  });

  /**
   * Helper function to manage button loading state
   * @param {HTMLElement} btn - Button element to update
   * @param {boolean} isLoading - Whether button should show loading state
   */
  function updateButtonState(btn, isLoading) {
    if (isLoading) {
      btn.setAttribute('disabled', true);
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-busy');
    }
  }
})();
